import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ImageTag from "../components/reuseable/imageTag";
import { getAuth, signOut } from "firebase/auth";

// import { toast } from "react-toastify";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  X,
  House,
  LogOut,
  LayoutDashboard,
  Users,
  Presentation,
  UsersRound,
  Database,
  Handshake,
  UserCheck,
  ClipboardList,
  RefreshCcw,
  Settings,
  Target,
  Newspaper,
  Bell,
  CalendarDays,
  MessageSquare,
  Wrench,
  Calculator,
  BookUser,
  HeartHandshake,
} from "lucide-react";
import { toast } from "react-toastify";
import { useStateValue } from "../context/StateProvider";
import { canUseTexting } from "../utils/texting";
import { selectUnassignedLeads } from "../utils/unassignedLeads";
import {
  listenListingLeads,
  listenBuyerLeads,
} from "../utils/firebasefunctions";
import { actionType } from "../context/reducer";
import { DEMO_VIEW_ROLES, applyDemoRole, isDemoUser } from "../utils/roles";
import useUnreadFeedCount from "../hooks/useUnreadFeedCount";

// Main scrolling menu — Settings is rendered separately, pinned at the bottom.
const menuItems = [
  {
    title: "Dashboard",
    icon: House,
    paths: ["/dashboard"],
    allowedusertypes: ["Admin", "Employee", "Supervisor", "Accountant"],
  },
  {
    title: "Feed",
    icon: Newspaper,
    paths: ["/dashboard/feed"],
    allowedusertypes: ["Admin", "Employee", "Supervisor", "Accountant"],
  },
  {
    title: "Deals Board",
    icon: Handshake,
    paths: ["/dashboard/deal-board"],
    allowedusertypes: ["Admin", "Employee", "Supervisor"],
  },
  {
    // Accountants don't work leads — no Pipeline for them (2026-07-31).
    title: "Pipeline",
    icon: Users,
    allowedusertypes: ["Admin", "Employee", "Supervisor"],
    children: [
      { title: "Listing Pipeline", paths: ["/dashboard/listings"] },
      { title: "Buyer Pipeline", paths: ["/dashboard/buyers"] },
      {
        title: "Unassigned Leads",
        paths: ["/dashboard/unassigned-leads"],
        allowedusertypes: ["Admin"],
        // Hidden entirely when nothing is waiting (see unassignedCount).
        showWhenCount: true,
      },
    ],
  },
  {
    title: "Transactions",
    icon: LayoutDashboard,
    paths: ["/dashboard/deals"],
    allowedusertypes: ["Admin", "Employee", "Supervisor", "Accountant"],
  },
  {
    title: "Commission Approvals",
    icon: Calculator,
    paths: ["/dashboard/commission-approvals"],
    allowedusertypes: ["Admin", "Accountant"],
  },
  {
    // Accountants get a spare experience — no Status Sheet, no Goals (2026-07-31).
    title: "Status Sheet",
    icon: ClipboardList,
    paths: ["/dashboard/status-sheet"],
    allowedusertypes: ["Admin", "Employee", "Supervisor"],
  },
  {
    // Rollout matches canUseTexting(): Team Lead + Client Care, plus agents
    // with the "Client care duties" switch (allowIf beats the role array).
    title: "Texting",
    icon: MessageSquare,
    paths: ["/dashboard/texting"],
    allowedusertypes: ["Admin", "Supervisor"],
    allowIf: (u) => canUseTexting(u),
  },
  {
    title: "Open Houses",
    icon: CalendarDays,
    paths: ["/dashboard/open-houses"],
    allowedusertypes: ["Admin", "Employee", "Supervisor"],
  },
  {
    title: "Contacts",
    icon: BookUser,
    paths: ["/dashboard/contact"],
    allowedusertypes: ["Admin", "Supervisor", "Employee"],
  },
  {
    title: "Team Directory",
    icon: UsersRound,
    paths: ["/dashboard/directory"],
    allowedusertypes: ["Admin", "Employee", "Supervisor", "Accountant"],
  },
  {
    title: "Vendor Directory",
    icon: Wrench,
    paths: ["/dashboard/vendors"],
    allowedusertypes: ["Admin", "Employee", "Supervisor", "Accountant"],
  },
  {
    title: "Follow-Ups",
    icon: HeartHandshake,
    paths: ["/dashboard/follow-ups"],
    allowedusertypes: ["Admin", "Employee"],
  },
  {
    title: "Team Performance",
    icon: Users,
    paths: ["/dashboard/team-management"],
    allowedusertypes: ["Admin", "Accountant"],
  },
  {
    title: "Goals",
    icon: Target,
    paths: ["/dashboard/goals"],
    allowedusertypes: ["Admin", "Employee"],
  },
];

// Pinned at the bottom of the sidebar, above LogOut.
const settingsItem = {
  title: "Settings",
  icon: Settings,
  paths: ["/dashboard/settings"],
  allowedusertypes: ["Admin", "Supervisor", "Employee"],
};

const Sidebar = ({ isMobileMenuOpen, setIsMobileMenuOpen, isCollapse }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [openGroups, setOpenGroups] = useState({});
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1024);
  const location = useLocation();
  const [{ user }, dispatch] = useStateValue();
  // "Unassigned Leads" only earns a spot in the sidebar when leads are
  // actually waiting for an agent — otherwise it just sits under Pipeline
  // saying zero (Henry, 2026-08-01). The sidebar watches the leads itself:
  // global state only holds them while you're on a pipeline page, so relying
  // on it made the item blink in and out as you moved around the app.
  const [sidebarListingLeads, setSidebarListingLeads] = useState([]);
  const [sidebarBuyerLeads, setSidebarBuyerLeads] = useState([]);
  const watchesUnassigned = user?.role === "Admin";
  useEffect(() => {
    if (!watchesUnassigned) return undefined;
    const unsubL = listenListingLeads((rows) => setSidebarListingLeads(rows || []));
    const unsubB = listenBuyerLeads((rows) => setSidebarBuyerLeads(rows || []));
    return () => {
      if (typeof unsubL === "function") unsubL();
      if (typeof unsubB === "function") unsubB();
    };
  }, [watchesUnassigned]);
  const unassignedCount = useMemo(
    () =>
      watchesUnassigned
        ? selectUnassignedLeads({
            listingLeads: sidebarListingLeads,
            buyerLeads: sidebarBuyerLeads,
          }).length
        : 0,
    [watchesUnassigned, sidebarListingLeads, sidebarBuyerLeads],
  );
  const unreadFeed = useUnreadFeedCount();

  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      const auth = getAuth();
      signOut(auth)
        .then(() => {
          dispatch({
            type: actionType.SET_USER,
            payload: [],
          });
          toast.success("Logged out successfully!");
          navigate("/");
        })
        .catch((error) => {
          toast.error("Couldn't sign out. Please try again.");
        });
    } catch (error) {
      toast.error("Logout failed. Please try again.");
      console.error("Logout Error:", error);
    }
  };

  // ✅ Corrected Active State Logic
  const isActive = (paths) => paths.some((path) => location.pathname === path);

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
      setIsOpen(true);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
    isCollapse(isOpen);
  };

  const sidebarVariants = {
    open: { width: "20%" },
    closed: { width: "80px", minWidth: "80px" },
  };

  const menuItemVariants = {
    open: { opacity: 1, x: 0 },
    closed: { opacity: 0, x: -10 },
  };

  const mobileSidebarVariants = {
    open: { y: 0, opacity: 1 },
    closed: { y: "-15%", opacity: 0 },
  };

  return (
    // <AnimatePresence>
    <>
      {(isLargeScreen || isMobileMenuOpen) && (
        <motion.aside
          className={`fixed top-0 left-0 z-40 h-screen w-full bg-gradient-to-b from-[#0f172a] to-[#1E293B] shadow-md flex flex-col ${
            isLargeScreen ? "" : "w-full"
          }`}
          initial={false}
          animate={isLargeScreen ? (isOpen ? "open" : "closed") : "open"}
          exit="closed"
          variants={isLargeScreen ? sidebarVariants : mobileSidebarVariants}
        >
          <div
            className={`flex items-center ${
              isOpen ? "justify-between" : "justify-center"
            } py-4 px-2 border-b border-[#FFFFFF1A] flex-shrink-0`}
          >
            {isOpen && (
              <motion.span
                variants={menuItemVariants}
                className="text-xl font-semibold w-full flex justify-center mt-4"
              >
                <ImageTag
                  path="/assets/logo.png"
                  classes="w-[160px]"
                  altText="login"
                />
              </motion.span>
            )}
            {isLargeScreen && (
              <button
                onClick={toggleSidebar}
                className="p-2 rounded-full bg-gray-200 hover:bg-gray-200"
              >
                {isOpen ? <ChevronLeft /> : <ChevronRight />}
              </button>
            )}
            {!isLargeScreen && (
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 rounded-full text-white hover:bg-gray-200"
              >
                <X />
              </button>
            )}
          </div>

          <div className="flex items-center gap-x-3 pl-5 py-3 flex-shrink-0">
            {user?.profilepicture && user?.profilepicture !== "" ? (
              <img
                src={user?.profilepicture}
                alt="user"
                className="size-10 rounded-full object-cover"
              />
            ) : (
              <div className="size-10 rounded-full bg-white flex justify-center items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="size-[20px]"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
                  />
                </svg>
              </div>
            )}
            {isOpen && (
              <Link to={"/dashboard/profile"}>
                <p className="text-white font-semibold text-md">
                  {user?.firstname} {user?.lastname}
                </p>
              </Link>
            )}
          </div>

          {isDemoUser(user) && (isOpen || !isLargeScreen) && (
            <div className="px-5 mb-2">
              <label className="text-textColor text-xs">View as</label>
              <select
                value={user.role}
                onChange={(e) =>
                  dispatch({
                    type: actionType.SET_USER,
                    payload: applyDemoRole(user, e.target.value),
                  })
                }
                className="w-full mt-1 rounded-md bg-white/10 text-white text-sm p-2 outline-none"
              >
                {DEMO_VIEW_ROLES.map((r) => (
                  <option key={r.value} value={r.value} className="text-black">
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <nav className="flex-1 overflow-y-auto py-2 space-y-0.5 min-h-0">
            {(isOpen || !isLargeScreen) && (
              <h3 className="text-textColor px-8 text-md">Main</h3>
            )}
            {menuItems.map((item) => {
              if (
                !item.allowedusertypes.includes(user?.role) &&
                !item.allowIf?.(user)
              )
                return null;

              // Grouped item (e.g. "Pipeline") — render a parent label with
              // indented child links beneath it.
              if (item.children) {
                const groupOpen = openGroups[item.title] !== false; // open by default
                return (
                  <div key={item.title} className="lg:w-full mx-auto">
                    <button
                      type="button"
                      onClick={() =>
                        setOpenGroups((g) => ({ ...g, [item.title]: !groupOpen }))
                      }
                      className="w-full flex items-center text-sm font-semibold text-slate-200 px-8 py-2 hover:text-white"
                    >
                      <item.icon className="h-[18px] w-[18px] min-w-[18px]" />
                      {(isOpen || !isLargeScreen) && (
                        <>
                          <motion.span variants={menuItemVariants} className="pl-3">
                            {item.title}
                          </motion.span>
                          <span className="ml-auto text-slate-400">
                            {groupOpen ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </span>
                        </>
                      )}
                    </button>
                    {groupOpen &&
                      (isOpen || !isLargeScreen) &&
                      item.children
                        .filter(
                          (child) =>
                            (!child.allowedusertypes ||
                              child.allowedusertypes.includes(user?.role)) &&
                            // Count-gated children (Unassigned Leads) stay
                            // out of the way until there's something to do.
                            (!child.showWhenCount || unassignedCount > 0),
                        )
                        .map((child) => (
                        <Link
                          key={child.title}
                          to={child.paths[0]}
                          className={`
      group relative flex items-center text-[13px] font-medium text-slate-300 pl-14 pr-4 py-1.5
      transition-all duration-300 ease-in-out
      ${
        isActive(child.paths)
          ? "bg-gradient-to-r from-themeColor to-linkColor !text-white"
          : "hover:bg-themeColor/10 hover:text-white"
      }
    `}
                          onClick={() => !isLargeScreen && setIsMobileMenuOpen(false)}
                        >
                          <span
                            className={`
        absolute left-0 top-1/2 -translate-y-1/2 w-[4px] rounded-r-md
        bg-gradient-to-b from-themeColor to-linkColor
        transition-all duration-300 ease-in-out
        ${isActive(child.paths) ? "h-[70%]" : "h-0 group-hover:h-[70%]"}
      `}
                          ></span>
                          {child.title}
                          {child.showWhenCount && unassignedCount > 0 && (
                            <span className="ml-2 text-[10px] font-bold bg-amber-500/90 text-white rounded-full px-1.5 py-0.5">
                              {unassignedCount}
                            </span>
                          )}
                        </Link>
                      ))}
                  </div>
                );
              }

              return (
                <div key={item.title} className="lg:w-full mx-auto">
                  <Link
                    to={item.paths[0]}
                    className={`
      group relative flex items-center text-sm font-medium text-slate-300 px-8 py-2
      transition-all duration-300 ease-in-out
      ${
        isActive(item.paths)
          ? "bg-gradient-to-r from-themeColor to-linkColor !text-white"
          : "hover:bg-themeColor/10 hover:text-white hover:pl-9"
      }
    `}
                    onClick={() => !isLargeScreen && setIsMobileMenuOpen(false)}
                  >
                    <span
                      className={`
        absolute left-0 top-1/2 -translate-y-1/2 w-[4px] rounded-r-md
        bg-gradient-to-b from-themeColor to-linkColor
        transition-all duration-300 ease-in-out
        ${isActive(item.paths) ? "h-[70%]" : "h-0 group-hover:h-[70%]"}
      `}
                    ></span>
                    <item.icon className="h-[18px] w-[18px] min-w-[18px]" />
                    {(isOpen || !isLargeScreen) && (
                      <motion.span variants={menuItemVariants} className="pl-3">
                        {item.title}
                      </motion.span>
                    )}
                    {item.title === "Feed" && unreadFeed > 0 && (
                      <>
                        {isOpen || !isLargeScreen ? (
                          <span className="ml-auto flex items-center gap-1 text-rose-100">
                            <Bell className="h-4 w-4" />
                            <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[11px] font-bold flex items-center justify-center">
                              {unreadFeed > 9 ? "9+" : unreadFeed}
                            </span>
                          </span>
                        ) : (
                          <span className="absolute top-2 right-3 w-2.5 h-2.5 rounded-full bg-rose-500" />
                        )}
                      </>
                    )}
                  </Link>
                </div>
              );
            })}
          </nav>

          <div className="flex-shrink-0 border-t border-[#FFFFFF1A] py-2">
            {settingsItem.allowedusertypes.includes(user?.role) && (
              <Link
                to={settingsItem.paths[0]}
                className={`
      group relative flex items-center text-sm font-medium text-slate-300 px-8 py-2
      transition-all duration-300 ease-in-out
      ${
        isActive(settingsItem.paths)
          ? "bg-gradient-to-r from-themeColor to-linkColor !text-white"
          : "hover:bg-themeColor/10 hover:text-white hover:pl-9"
      }
    `}
                onClick={() => !isLargeScreen && setIsMobileMenuOpen(false)}
              >
                <span
                  className={`
        absolute left-0 top-1/2 -translate-y-1/2 w-[4px] rounded-r-md
        bg-gradient-to-b from-themeColor to-linkColor
        transition-all duration-300 ease-in-out
        ${
          isActive(settingsItem.paths)
            ? "h-[70%]"
            : "h-0 group-hover:h-[70%]"
        }
      `}
                ></span>
                <settingsItem.icon className="h-[18px] w-[18px] min-w-[18px]" />
                {(isOpen || !isLargeScreen) && (
                  <motion.span variants={menuItemVariants} className="pl-3">
                    {settingsItem.title}
                  </motion.span>
                )}
              </Link>
            )}
            <div
              className="group relative flex items-center text-sm font-medium text-slate-300 px-8 py-2
      transition-all duration-300 ease-in-out hover:bg-themeColor/90 hover:text-white hover:pl-9 cursor-pointer"
              onClick={handleLogout}
            >
              <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[4px] rounded-r-md bg-gradient-to-b from-themeColor to-linkColor transition-all duration-300 ease-in-out h-0 group-hover:h-[70%]"></span>
              <LogOut className="h-[18px] w-[18px] min-w-[18px]" />
              {isOpen && (
                <motion.span variants={menuItemVariants} className="pl-3">
                  LogOut
                </motion.span>
              )}
            </div>
          </div>
        </motion.aside>
      )}
    </>
    // </AnimatePresence>
  );
};

export default Sidebar;
